import { APP } from 'src/app/config/app';
import { ITimelineInterval, ITimelineLane, ITimelineStackItem } from 'src/app/models';

function overlaps(a: ITimelineInterval, b: ITimelineInterval): boolean {
  return a.startMinutes < b.endMinutes && b.startMinutes < a.endMinutes;
}

export function spansOverlap(first: ITimelineInterval[], second: ITimelineInterval[]): boolean {
  return first.some((a) => second.some((b) => overlaps(a, b)));
}

function byStart<T extends { startMinutes: number }>(items: T[]): number[] {
  return items.map((_, index) => index).sort((a, b) => items[a].startMinutes - items[b].startMinutes || a - b);
}

export function stackLanes(intervals: ITimelineInterval[], baseHeight: number): { lanes: ITimelineLane[]; height: number } {
  const lanes: ITimelineLane[] = intervals.map(() => ({ top: 0, height: baseHeight }));
  const clusters: number[][] = [];
  let clusterEnd = -Infinity;
  byStart(intervals).forEach((index) => {
    if (intervals[index].startMinutes >= clusterEnd) {
      clusters.push([]);
    }
    clusters[clusters.length - 1].push(index);
    clusterEnd = Math.max(clusterEnd, intervals[index].endMinutes);
  });
  clusters
    .filter((cluster) => cluster.length > 1)
    .forEach((cluster) => {
      const laneEnds: number[] = [];
      cluster.forEach((index) => {
        let lane = laneEnds.findIndex((laneEnd) => laneEnd <= intervals[index].startMinutes);
        if (lane < 0) {
          lane = laneEnds.length;
        }
        laneEnds[lane] = intervals[index].endMinutes;
        lanes[index] = {
          top: lane * (APP.SCHEDULE_TRIP_CHIP_MIN_HEIGHT_PX + APP.SCHEDULE_TRIP_CHIP_GAP_PX),
          height: APP.SCHEDULE_TRIP_CHIP_MIN_HEIGHT_PX,
        };
      });
    });
  return { lanes, height: Math.max(baseHeight, ...lanes.map((lane) => lane.top + lane.height)) };
}

export function placeStack(items: ITimelineStackItem[]): number[] {
  const tops: number[] = items.map(() => 0);
  const placed: number[] = [];
  byStart(items).forEach((index) => {
    const item = items[index];
    const neighbours = placed.filter((other) => spansOverlap(item.spans, items[other].spans));
    const fits = (top: number) =>
      neighbours.every(
        (other) => top + item.height + APP.SCHEDULE_BLOCK_GAP_PX <= tops[other] || tops[other] + items[other].height + APP.SCHEDULE_BLOCK_GAP_PX <= top,
      );
    const candidates = [0, ...neighbours.map((other) => tops[other] + items[other].height + APP.SCHEDULE_BLOCK_GAP_PX)].sort((a, b) => a - b);
    tops[index] = candidates.find(fits)!;
    placed.push(index);
  });
  return tops;
}
